/**
 * Service: Admin (roles, invites, reviews)
 * Wraps admin management operations and the send-admin-invite edge function.
 */
import { supabase } from "@/integrations/supabase/client";

/* ── User Roles ── */

export async function listTenantMembers(tenantId: string) {
  return supabase
    .from("user_roles")
    .select("id, user_id, role")
    .eq("tenant_id", tenantId)
    .in("role", ["admin", "approver"]);
}

// Deprecated: prefer listTenantMembers — kept for back-compat with existing call sites.
export const listTenantAdmins = listTenantMembers;

export async function revokeUserRole(roleId: string, reason?: string) {
  return supabase.rpc("revoke_user_role", { p_role_id: roleId, p_reason: reason ?? null });
}
// Deprecated alias — prefer revokeUserRole.
export const removeAdminRole = revokeUserRole;

/**
 * B2 INVARIANT — privileged-roles-only lookup.
 * This helper answers "does this account hold any PRIVILEGED role?" and MUST
 * stay filtered to admin/approver/super_admin. Do NOT widen to include
 * 'consumer' or 'creator': the auth-context callers use the presence of a
 * result to decide "route this session into the admin surface". Consumer role
 * lookups belong in a separate helper (see hasConsumerRole flow in AdminSidebar).
 * If the privileged-role set changes, mirror the change in public.is_privileged_role().
 */
export async function checkUserRoles(userId: string) {
  return supabase
    .from("user_roles")
    .select("tenant_id, role")
    .eq("user_id", userId)
    .in("role", ["admin", "super_admin", "approver"])
    .order("role", { ascending: true });
}

export async function getSuperAdminTenant(userId: string) {
  return supabase
    .from("user_roles")
    .select("tenant_id")
    .eq("user_id", userId)
    .eq("role", "super_admin")
    .maybeSingle();
}

/* ── Admin Invites ── */

export async function listTenantInvites(tenantId: string) {
  return supabase
    .from("tenant_admin_invites")
    .select("id, email, status, created_at, intended_role, accepted_by_user_id")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
}

export async function cancelInvite(inviteId: string) {
  return supabase.from("tenant_admin_invites").delete().eq("id", inviteId);
}

/* ── Edge Function: send admin invites (critical workflow) ── */

export async function sendAdminInvites(payload: {
  emails: string[];
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  intended_role?: "admin" | "approver";
  custom_subject?: string;
  custom_body_html?: string;
}) {
  return supabase.functions.invoke("send-admin-invite", { body: payload });
}

/* ── Admin Reviews ── */

export async function listAdminReviews(tenantId: string) {
  return supabase
    .from("admin_reviews")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("reviewed_at", { ascending: false });
}
